/** The portal's English copy; every other language is keyed the same. */
export const en = {
  shell: {
    portal: "Client portal",
    nav: {
      home: "Home",
      case: "My case",
      caseShort: "Case",
      tasks: "Tasks",
      documents: "Documents",
      profile: "My profile",
      profileShort: "Profile"
    },
    signOut: "Sign out",
    language: "Language",
    previewing:
      "You're viewing {{name}}'s portal as they see it. Read-only: nothing here is saved or sent.",
    exitPreview: "Exit preview",
    privacy: "Only you and your legal team can see your information.",
    notClient: "This account isn't set up for a client portal",
    notClientBody:
      "Ask your legal team to invite you, or sign in with the email they invited.",
    useDifferentEmail: "Use a different email",
    cantPreview: "This portal can't be previewed",
    loadFailed: "We couldn't load your portal. Try again in a moment.",
    retry: "Try again"
  },
  greeting: {
    morning: "Good morning, {{name}}",
    afternoon: "Good afternoon, {{name}}",
    evening: "Good evening, {{name}}",
    withTasks_one: "Here's the one thing we need from you.",
    withTasks_other: "Here's what we need from you.",
    stageLine: {
      started: "Your case is getting started.",
      gathering: "We're gathering information for your case.",
      review: "Your attorney is reviewing your case.",
      filed: "Your case has been filed with USCIS.",
      decision: "Your case is at the decision stage."
    },
    allCaughtUp:
      "You're all caught up. We'll let you know when we need anything."
  },
  tasks: {
    title: "What we need from you",
    count_one: "1 task",
    count_other: "{{count}} tasks",
    none: "Nothing right now",
    noneBody:
      "When your legal team needs something from you, it shows up here.",
    progress: "{{answered}} of {{total}} answered",
    minutesLeft_one: "about 1 minute left",
    minutesLeft_other: "about {{count}} minutes left",
    due: "due {{date}}",
    dueToday: "due today",
    overdue: "was due {{date}}",
    changesRequested: "Your legal team asked you to update a few answers",
    start: "Start",
    continue: "Continue",
    update: "Update answers",
    opening: "Opening…",
    openFailed:
      "We couldn't open this task. Use the link in your email, or try again.",
    previewOnly: "Tasks open only for the client.",
    sign: "Review and sign",
    ink: "Start",
    notYet: "Not yet",
    signMeta: "About {{minutes}} minutes",
    inkMeta: "Print, sign in ink, send a photo · about {{minutes}} minutes",
    upcoming: "Opens when it's ready for your signature",
    sendAgain: "Your legal team asked you to send this again"
  },
  case: {
    title: "Your case",
    titlePlural: "Your cases",
    fullTimeline: "Full timeline",
    now: "What's happening now",
    onHold:
      "Your case is paused for now. Your legal team will tell you when it picks up again.",
    approved: "Approved",
    decision: "Decision made",
    receipts: "USCIS receipts",
    noReceipts: "Your receipt number appears here once USCIS sends it.",
    checkStatus: "Check USCIS status",
    checking: "Checking…",
    checkedAt: "Checked {{when}}",
    statusFailed: "USCIS isn't answering right now. Try again later.",
    details: "Case details",
    caseType: "Case type",
    opened: "Opened",
    priorityDate: "Priority date",
    updates: "Updates",
    noUpdates: "Updates about your case will appear here.",
    none: "No cases to show yet",
    noneBody:
      "When your legal team opens your case, you'll be able to follow it here.",
    back: "All cases",
    notFound: "We couldn't find this case.",
    step: "Step {{n}} of {{total}}",
    startedOn: "started {{date}}",
    nowChip: "Now",
    waitingOnYou: "Waiting on you",
    open: "Open",
    attorney: "Attorney",
    receiptNumber: "Receipt number",
    receiptLater: "Shown here after filing",
    priorityLater: "Set when USCIS receives it",
    questions: "Questions about your case?",
    emailLead: "Email {{name}}"
  },
  milestone: {
    rfe: "USCIS asked for more evidence (a Request for Evidence). {{lead}} is preparing the response and will tell you if anything is needed from you.",
    interview:
      "USCIS scheduled an interview. {{lead}} will help you prepare and tell you what to bring.",
    biometrics:
      "USCIS is at the biometrics step: fingerprints and a photo at a USCIS office. Bring your appointment notice and photo ID."
  },
  stageAbout: {
    started:
      "Your legal team opens your case and works out what they need from you.",
    gathering:
      "We collect your answers and documents: who you are, your history, and the evidence your case needs.",
    review:
      "Your attorney reviews everything, prepares your forms and letters, and may ask you a few follow-up questions.",
    filed:
      "We send your case to USCIS. A receipt number appears here once USCIS sends it, and you can check its status from this page.",
    decision:
      "USCIS approves the case, asks for more evidence (a Request for Evidence), or denies it. We'll talk you through what comes next."
  },
  stage: {
    started: "Getting started",
    gathering: "Gathering information",
    review: "Attorney review",
    filed: "Filed with USCIS",
    decision: "Decision"
  },
  explainer: {
    yourTeam: "Your legal team",
    yourTeamLower: "your legal team",
    started:
      "{{lead}} is setting up your case. Anything we need from you will appear on this page.",
    gathering:
      "{{lead}} is collecting your answers and documents. Once they're in, {{leadFirst}} reviews everything and starts preparing your filing.",
    review:
      "{{lead}} is reviewing your information and preparing your filing. You may get a few follow-up questions.",
    filed:
      "Your case was filed with USCIS. USCIS usually mails a receipt notice first and may then schedule a biometrics appointment. Processing times vary by case type.",
    decision:
      "USCIS has made a decision on your case. {{lead}} will go over what it means and any next steps with you."
  },
  updates: {
    opened: "Your case was opened",
    filed: "Your case was filed with USCIS",
    approved: "USCIS approved your case",
    decided: "USCIS made a decision on your case",
    reopened: "Your case was reopened",
    receipt: "USCIS receipt {{receipt}} added",
    receipt_unnamed: "A USCIS receipt was added",
    questions_sent: "We sent you questions to answer",
    answers_received: "We received your answers",
    changes_requested: "We asked you to update a few answers",
    answers_reviewed: "Your answers were reviewed"
  },
  comingUp: {
    title: "Coming up",
    taskDue: "{{title}} due",
    allDay: "All day"
  },
  team: {
    title: "Your legal team",
    email: "Email {{name}}"
  },
  reminders: {
    title: "Keep in mind",
    expiresIn_one: "{{subject}} expires tomorrow",
    expiresIn_other: "{{subject}} expires in {{count}} days",
    expiresToday: "{{subject}} expires today",
    expired: "{{subject}} end date on file was {{date}}",
    expiresHint: "Tell your legal team before you renew it or plan any travel.",
    expiredHint:
      "If you've received a newer document, upload it so your legal team has it.",
    yours: {
      status: "your {{status}} status",
      immigration_status_expiration: "your immigration status",
      i94_expiration: "your I-94",
      passport_expiration: "your passport",
      ead_expiration: "your work permit (EAD)",
      advance_parole_expiration: "your advance parole",
      visa_expiration: "your visa"
    },
    theirs: {
      immigration_status_expiration: "{{name}}'s immigration status",
      i94_expiration: "{{name}}'s I-94",
      passport_expiration: "{{name}}'s passport",
      ead_expiration: "{{name}}'s work permit (EAD)",
      advance_parole_expiration: "{{name}}'s advance parole",
      visa_expiration: "{{name}}'s visa"
    }
  },
  documents: {
    title: "Documents",
    subtitle: "What you've sent us and what your legal team shared with you.",
    upload: "Upload a document",
    takePhoto: "Take a photo",
    uploading: "Uploading {{name}}…",
    uploaded: "{{name}} sent to your legal team",
    uploadFailed: "{{name}} didn't upload. Try again.",
    fromYou: "From you",
    fromTeam: "From your legal team",
    open: "Open",
    remove: "Remove",
    removed: "Removed {{name}}",
    removeFailed: "Couldn't remove it. Try again.",
    none: "No documents yet",
    noneBody:
      "Upload documents your legal team asks for, or check back for files they share with you.",
    previewOnly: "Uploads are off in the preview.",
    checking: "Checking {{name}}…",
    looksGood: "{{name}} looks good",
    weRead: "We read this from it:",
    mayBeHard: "{{name}} may be hard to read",
    replace: "Replace it",
    keep: "Keep it"
  },
  sign: {
    from: "From {{name}}",
    inkWhy:
      "USCIS needs a handwritten signature on paper forms, so this page can't be signed on screen.",
    read: "Read the document",
    readHint: "Read every page before you sign.",
    add: "Add your signature",
    draw: "Draw",
    type: "Type",
    drawHint: "Sign here with your finger or mouse",
    clear: "Clear",
    fullName: "Your full name",
    consent:
      "I agree to sign this document electronically, and that my electronic signature counts the same as my handwritten one.",
    signAndSend: "Sign and send to {{name}}",
    signing: "Signing…",
    audit:
      "We email you a signed copy. Your signature, the time, and a fingerprint of the document are recorded so it can be verified later.",
    needSignature: "Draw or type your signature first.",
    needConsent: "Check the box to agree to sign electronically.",
    failed: "Couldn't sign it. Try again.",
    signed: "Signed",
    signedBody: "You signed this on {{date}}. We emailed you a copy.",
    signedNoCopy: "You signed this on {{date}}.",
    downloadSigned: "Download your signed copy",
    print: "Print the page",
    download: "Download the page to sign (PDF)",
    noPrinter: "No printer? Ask your legal team to mail it to you.",
    inkSign: "Sign in black ink",
    inkBox: "Sign in the box labeled “{{box}}” on page {{page}}.",
    inkBoxPage: "Sign the signature box on page {{page}}.",
    photo: "Take a photo of the signed page",
    photoHint: "Lay it flat in good light, with the whole page in view.",
    takePhoto: "Take a photo",
    chooseFile: "Choose a file",
    retake: "Retake",
    checking: "Checking your photo…",
    found: "Signature found in the box",
    notFound: "We couldn't see a signature in the box",
    readable: "Whole page visible and readable",
    notReadable: "Part of the page is hard to read",
    unchecked: "We couldn't check this photo, but you can still send it.",
    send: "Send to {{name}}",
    sendAnyway: "Send it anyway",
    sending: "Sending…",
    uploadFailed: "That photo didn't upload. Try again.",
    keepOriginal: "Keep the original. {{name}} may ask you to mail it.",
    sent: "Sent to {{name}}",
    sentBody:
      "{{name}} will check it and let you know if anything else is needed. Keep the original.",
    completed: "Done",
    completedBody: "{{name}} accepted your signed page.",
    returned: "{{name}} asked you to send it again:",
    upcoming: "Not ready yet",
    upcomingBody:
      "{{name}} will let you know when it's ready for your signature.",
    gone: "This document doesn't need your signature any more.",
    back: "Back to tasks",
    previewOnly: "Signing is off in the preview.",
    viewer: "Document to sign",
    changed:
      "This document changed after it was sent. Your legal team will send it again."
  },
  profile: {
    subtitle:
      "This is what goes on your immigration forms. If something is wrong or has changed, suggest a correction and {{name}} will review it.",
    about: "About you",
    contact: "How we reach you",
    lived: "Where you've lived",
    worked: "Where you've worked",
    studied: "Your education",
    noneOnFile: "Nothing on file yet",
    notOnFile: "Not on file",
    historyHint: "Something missing or wrong here?",
    tellUs: "Tell {{name}}",
    suggest: "Suggest a correction",
    add: "Add",
    waiting: "Waiting for {{name}}'s review",
    withdraw: "Withdraw",
    withdrawn: "Suggestion withdrawn",
    updated: "Updated",
    kept: "{{name}} kept what's on file.",
    keptWhy: "{{name}} kept what's on file: {{reason}}",
    note: "Anything {{name}} should know?",
    notePlaceholder: "For example: my passport has the correct spelling.",
    addressPlaceholder: "If you moved, tell us when.",
    historyLabel: "What's missing or wrong?",
    historyPlaceholder:
      "For example: I lived at 12 Elm St, Austin, TX from June 2019 to May 2020.",
    yourNotes: "Notes you sent",
    attach: "Attach a document",
    attachHint: "A photo or PDF that shows the correct information.",
    attaching: "Uploading {{name}}…",
    attachFailed: "That file didn't upload. Try again.",
    removeAttachment: "Remove attachment",
    cancel: "Cancel",
    send: "Send to {{name}}",
    sending: "Sending…",
    sent: "Sent to {{name}} for review",
    failed: "Couldn't send it. Try again.",
    unchanged: "That's what we already have. Change a value first.",
    previewOnly: "Suggestions are off in the preview.",
    present: "now",
    topic: {
      legal_name: "Legal name",
      other_names: "Other names you've used",
      birthday: "Date of birth",
      birthplace: "Born in",
      citizenships: "Citizenship",
      marital_status: "Marital status",
      email: "Email",
      phone: "Phone",
      physical_address: "Home address",
      mailing_address: "Mailing address",
      history: "Your history"
    },
    field: {
      first_name: "First name",
      middle_name: "Middle name",
      last_name: "Last name",
      other_names: "Other names, separated by commas",
      birthday: "Date of birth",
      city_of_birth: "City",
      state_of_birth: "State or province",
      country_of_birth: "Country",
      citizenships: "Countries, separated by commas",
      marital_status: "Marital status",
      email: "Email",
      phone: "Phone",
      street: "Street and unit",
      city: "City",
      state: "State",
      zip_code: "ZIP or postal code",
      country: "Country"
    },
    marital: {
      single: "Single, never married",
      married: "Married",
      divorced: "Divorced",
      widowed: "Widowed",
      separated: "Legally separated",
      annulled: "Marriage annulled"
    }
  },
  signIn: {
    title: "Sign in to your portal",
    sentTitle: "Check your email",
    email: "Email",
    send: "Email me a sign-in link",
    sending: "Sending…",
    hint: "No password needed. We'll send a link to this email that signs you in.",
    sent: "If {{email}} has a client portal, a sign-in link is on its way. It works once and expires in an hour.",
    sendAnother: "Use a different email",
    failed: "Couldn't send the link. Try again in a minute.",
    staff: "Work at a law firm?",
    staffLink: "Sign in here"
  },
  welcome: {
    signingIn: "Signing you in…",
    expired: "This link has expired",
    used: "This link was already used",
    invalid: "This link isn't valid",
    revoked: "This link no longer works",
    off: "Portal access is turned off",
    offBody: "Ask your legal team to turn it back on.",
    getNew: "Enter your email and we'll send you a fresh sign-in link."
  },
  intake: {
    language: "Language",
    showEnglish: "Show in English",
    showTranslated: "Show in {{language}}",
    translating: "Translating the questions…",
    translationFailed:
      "We couldn't translate the questions right now, so they're in English.",
    answerHint:
      "Answer in English if you can. If not, answer in your own language and your legal team will translate.",
    defaultNote:
      "Take your time completing this intake. Make sure all information matches your official documents.",
    defaultTitle: "Immigration Client Intake",
    start: "Start",
    resume: "Resume",
    fix_one: "Fix {{count}} answer →",
    fix_other: "Fix {{count}} answers →",
    takes_one: "Takes about {{count}} minute",
    takes_other: "Takes about {{count}} minutes",
    left_one: "About {{count}} minute left",
    left_other: "About {{count}} minutes left",
    about_one: "About {{count}} minute",
    about_other: "About {{count}} minutes",
    othersSaved_one: " · the other answer is saved",
    othersSaved_other: " · the other {{count}} answers are saved",
    needsChanges_one:
      "Your attorney reviewed your answers and needs one change.",
    needsChanges_other:
      "Your attorney reviewed your answers and needs {{count}} changes.",
    attorney: "Attorney",
    yourAttorney: "Your attorney",
    yourAttorneyLower: "your attorney",
    yourFirm: "Your law firm",
    you: "You",
    welcomeBack: "Welcome back",
    welcomeBackName: "Welcome back, {{name}}.",
    resumeBody:
      "You've already started this intake. Would you like to continue where you left off?",
    continueWhereLeft: "Continue where I left off",
    reviewFromStart: "Review from the beginning",
    ok: "OK",
    skip: "Skip",
    continue: "Continue",
    submit: "Submit",
    sendTo: "Send to {{name}}",
    yes: "Yes",
    no: "No",
    previousStep: "Previous step",
    nextStep: "Next step",
    displaySettings: "Display settings",
    typeAnswer: "Type your answer here...",
    yourAnswer: "Your answer",
    typeOrSelect: "Type or select an option",
    noMatches: "No matching options",
    street: "Street address",
    city: "City",
    state: "State / Province",
    statePlaceholder: "State",
    postal: "Postal / ZIP Code",
    postalPlaceholder: "Postal code",
    country: "Country",
    dragDrop: "Drag and drop your files",
    uploadingShort: "Uploading...",
    upload: "Upload",
    uploadMore: "Upload More",
    uploadedFiles: "Uploaded files",
    uploadingDocument: "Uploading document",
    uploaded: "Uploaded",
    uploadedBody_one: "Your file was uploaded successfully.",
    uploadedBody_other: "{{count}} files uploaded successfully.",
    someFailed: "Some uploads failed",
    uploadFailed: "Upload failed",
    pleaseTryAgain: "Please try again.",
    saved: "Saved",
    savedBody: "You can close this page and come back using the same link.",
    couldntSave: "Couldn’t save",
    submitted: "Submitted",
    submittedBody: "Your answers were sent successfully.",
    couldntSubmit: "Couldn’t submit",
    thankYou: "Thank you!",
    thankYouBody:
      "Your legal team has received your responses and will review them shortly. You can safely close this tab.",
    review: "Review your answers",
    reviewBody:
      "Please review your answers before submitting. You can edit anything that needs a change.",
    reviewGroupBody: "You can edit or remove any entry, or add another.",
    edit: "Edit",
    remove: "Remove",
    notAnswered: "Not answered",
    notProvided: "Not provided",
    noEntries: "No entries.",
    noEntriesAdded: "No entries added.",
    noEntriesYet: "No entries added yet.",
    noEntriesSubmitted: "No entries were submitted.",
    noDetails: "No details provided yet.",
    noDocChanges: "No document changes requested.",
    noDocs: "No documents available.",
    leaveComment: "Leave a comment...",
    addComment: "Add a comment",
    addReply: "Add a reply",
    cancel: "Cancel",
    youSent: "You sent",
    youAnswered: "You answered:",
    addAnother: "Add another?",
    loadFailed: "Failed to load intake",
    oops: "Oops, something went wrong.",
    persists: "If the problem persists, please contact your law firm.",
    tryAgain: "Try again",
    attention_one: "One upload needs attention",
    attention_other: "{{count}} uploads need attention",
    attentionBody: "Your attorney may ask you to send these again.",
    sendAnyway: "Send anyway",
    savesAuto: "Your progress saves automatically",
    prefilledCount_one: "{{count}} answer already filled in",
    prefilledCount_other: "{{count}} answers already filled in",
    progressOf: "{{current}} of {{total}}",
    savedAt: "Saved {{time}}",
    backToPortal: "Back to your portal",
    outline: {
      questions_one: "{{count}} question",
      questions_other: "{{count}} questions",
      filled_one: "{{count}} filled in",
      filled_other: "{{count}} filled in",
      minutes_one: "{{count}} min",
      minutes_other: "{{count}} min",
      done: "Done"
    },
    prefill: {
      ask: "Is this right?",
      yes: "Yes, that's right",
      change: "Change it",
      fromDocs: "From your documents",
      onFile: "Already on file"
    },
    date: {
      day: "Day",
      month: "Month",
      year: "Year",
      pickMonth: "Choose a month",
      echo: "You wrote: {{date}}",
      invalid: "That date doesn't exist. Check the day and month."
    },
    ph: {
      email: "name@example.com",
      phone: "+1 555 123 4567",
      givenName: "As it appears on your passport",
      familyName: "As it appears on your passport",
      middleName: "Leave blank if you don't have one",
      otherNames: "Any other names you have used",
      fullName: "Full name",
      number: "As printed on the document",
      city: "City",
      country: "Country",
      employer: "Company or organization",
      jobTitle: "Your job title",
      school: "School or university",
      website: "https://",
      longText: "Type your answer here…",
      answer: "Type your answer here…"
    },
    group: {
      tellUs: "Tell us about your {{name}}.",
      tellUsAddresses: "Tell us about your address history.",
      tellUsEmployment: "Tell us about your employment history.",
      tellUsStatuses: "Tell us about your immigration status history.",
      tellUsTrips: "Tell us about your U.S. entry and exit history.",
      tellUsDependents: "Tell us about your dependents.",
      leadIn: "Do you have any {{name}} to add?",
      leadInAddresses: "Do you have any previous addresses to add?",
      leadInEmployment: "Do you have any employment history to add?",
      leadInStatuses: "Do you have any immigration status history to add?",
      leadInTrips: "Do you have any U.S. entries or exits to add?",
      addAnother: "Add another {{name}}?",
      addAnotherAddress: "Add another address?",
      addAnotherEmployer: "Add another employer?",
      addAnotherStatus: "Add another status?",
      addAnotherTrip: "Add another trip?",
      review: "Review your {{name}}",
      reviewAddresses: "Review your addresses",
      reviewEmployers: "Review your employers",
      reviewStatuses: "Review your statuses",
      reviewTrips: "Review your trips",
      entries: "entries"
    }
  },
  common: {
    loading: "Loading…",
    today: "today",
    yesterday: "yesterday",
    daysAgo_one: "1 day ago",
    daysAgo_other: "{{count}} days ago"
  }
};

export type PortalStrings = typeof en;
